import Link from 'next/link'
import { useState, useEffect, useContext, useRef } from 'react'
import { useRouter } from 'next/router'
import PropTypes from 'prop-types'

// Internal context
import { NavigationContext } from '../../context/NavigationContext'

// Internal components
import Svg from '../Svg/Svg'

/**
 * SideMenuLink
 *
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */

const SideMenuLink = (props) => {
  const { url, label, scroll = false } = props
  return (
    <Link href={url} scroll={scroll}>
      <a className="SideMenu-link">
        <span className="SideMenu-label">{label}</span>
      </a>
    </Link>
  )
}

SideMenuLink.defaulProps = {
  label: 'LABEL',
  url: '#',
  scroll: false
}

SideMenuLink.propTypes = {
  label: PropTypes.string,
  url: PropTypes.string,
  scroll: PropTypes.bool
}

/**
 * SideMenu
 *
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const SideMenu = (props) => {
  const { menu } = props

  // Router
  const router = useRouter()

  // Context
  const navigationContext = useContext(NavigationContext)

  // Refs
  const sideMenuListRef = useRef()

  // State
  const [isExpanded, setIsExpanded] = useState(false)
  const [openStateIndex, setOpenStateIndex] = useState({
    childList: 0,
    subChildList: 0
  })

  // State Classes
  const expandedClass = 'is-expanded'
  const activeClass = 'is-active'
  const openClass = 'is-open'

  useEffect(() => {
    if (navigationContext.routeChange === true) setIsExpanded(false)
  }, [navigationContext.routeChange])

  useEffect(() => {
    const updateMinHeight = () =>
      setTimeout(() => {
        const layoutInfoElement = document.querySelector('.LayoutInfo')
        const sideMenuHeight = sideMenuListRef?.current?.clientHeight
        layoutInfoElement?.style?.setProperty('--min-height', `${sideMenuHeight > 650 ? sideMenuHeight : 650}px`)
      }, 50)

    updateMinHeight()
    sideMenuListRef?.current?.addEventListener('click', updateMinHeight)
    return () => {
      sideMenuListRef?.current?.removeEventListener('click', updateMinHeight)
      const layoutInfoElement = document.querySelector('.LayoutInfo')
      layoutInfoElement?.style?.setProperty('--min-height', '1650px')
    }
  }, [])

  const createChildMenu = (item, url, children, type) => {
    const defaultState = router.asPath.includes(item.uri)
    const [isOpen, setIsOpen] = useState(defaultState)

    useEffect(() => {
      if (defaultState === true) openStateIndex[type] = item.id
      setIsOpen(true)
    }, [])

    const toggleMenuState = (id, type, state, forced = false) => {
      openStateIndex[type] = openStateIndex[type] !== id || forced ? item.id : 0
      setOpenStateIndex(openStateIndex)
      setIsOpen(state)
    }

    return (
      <>
        <button className={`SideMenu-toggle ${openStateIndex[type] === item.id ? openClass : ''}`}>
          <Link href={`/${url}`} scroll={false} passHref>
            <span className="SideMenu-label" onClick={() => toggleMenuState(item.id, type, !isOpen, true)}>
              {item.label}
            </span>
          </Link>
          <span className="SideMenu-toggleContent" onClick={() => toggleMenuState(item.id, type, !isOpen)}>
            <Svg id={'icon-chevron-down'} modifiers={['SideMenu-toggleIcon']} />
          </span>
        </button>

        <ul className={`SideMenu-list SideMenu-list--${type}`}>
          {children &&
            children.map((subitem) => {
              const subChildren = subitem.items && subitem.items.length !== 0 ? subitem.items : false
              const subLabel = subitem.label

              return (
                <li
                  className={`SideMenu-item ${router.asPath.includes(subitem.uri) ? activeClass : ''}`}
                  key={`SideMenu-item--${subitem.id}`}
                >
                  {subChildren ? (
                    <> {createChildMenu(subitem, subitem.uri, subChildren, 'subChildList')} </>
                  ) : (
                    <SideMenuLink url={subitem.url} label={subLabel} />
                  )}
                </li>
              )
            })}
        </ul>
      </>
    )
  }

  return (
    <nav className={`SideMenu ${isExpanded ? expandedClass : ''}`}>
      <div className="SideMenu-inner">
        <div className="SideMenu-menu">
          <button className="SideMenu-menuToggle" onClick={() => setIsExpanded(!isExpanded)}>
            <Svg id={'icon-menu'} modifiers={['SideMenu-menuIcon']} />
            <span className="SideMenu-menuLabel">{menu.label}</span>
            <Svg id={'icon-chevron-down'} modifiers={['SideMenu-menuOpenIcon']} />
          </button>
        </div>

        <ul className="SideMenu-list" ref={sideMenuListRef}>
          <li className="SideMenu-item is-home is-active">
            <Link href={`${menu.url}`} scroll={false}>
              <a className="SideMenu-link">
                <span className="SideMenu-label">{menu.label}</span>
                <Svg id={'icon-homefull'} modifiers={['SideMenu-homeIcon']} />
              </a>
            </Link>
          </li>

          {menu.items &&
            menu.items.map((item) => {
              const children = item.items.length !== 0 ? item.items : false
              const label = item.label
              const activeState = router.asPath.includes(item.slug) ? activeClass : ''

              return (
                <li className={`SideMenu-item ${activeState}`} key={`SideMenu-item--${item.id}`}>
                  {children ? (
                    <> {createChildMenu(item, item.uri, children, 'childList')} </>
                  ) : (
                    <SideMenuLink url={item.url} label={label} />
                  )}
                </li>
              )
            })}
        </ul>
      </div>
      <div className="SideMenu-overlay" onClick={() => setIsExpanded(!isExpanded)} />
    </nav>
  )
}

SideMenu.propTypes = {
  menu: PropTypes.object.isRequired,
  modifiers: PropTypes.array
}

export default SideMenu
